import { createColumnHelper } from '@tanstack/react-table';
import { Calendar, Download, MoreHorizontal } from 'lucide-react';
import { useMemo } from 'react';

import { useGetStudentSubscriptionUpcomingInvoices } from '~entities/student/api/get-student-subscription-upcoming-invoices';
import type { SubscriptionInvoice } from '~entities/student/modal/payment';
import { useCurrentUserRole } from '~entities/users/hooks/use-current-user-role';

import { CancelInvoice } from '~features/students/ui/student-payment/cancel-invoice';

import { useGetCurrency } from '~shared/api/currency-api';
import {
  EnrollioTable,
  EnrollioTableSearch,
} from '~shared/components/enrollio-table';
import { exportToCSV } from '~shared/helpers/csv';
import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~shared/shadcn/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '~shared/shadcn/ui/dropdown-menu';

import { EditUpcomingCharges } from './edit-upcoming-charges';

const columnHelper = createColumnHelper<SubscriptionInvoice>();

interface ViewUpcomingChargesProps {
  studentId: string;
}

export const ViewUpcomingCharges = ({
  studentId,
}: ViewUpcomingChargesProps) => {
  const { data: currencyDetails } = useGetCurrency();
  const { hasAdminAccess } = useCurrentUserRole();
  const { data, isLoading } =
    useGetStudentSubscriptionUpcomingInvoices(studentId);
  const columns = useMemo(
    () => [
      columnHelper.accessor('upcomingChargeDate', { header: 'Date' }),
      columnHelper.accessor('productName', { header: 'Product' }),
      columnHelper.accessor('type', { header: 'Type' }),
      columnHelper.accessor('paymentFrequency', {
        header: 'Payment frequency',
      }),
      columnHelper.accessor('amount', {
        header: 'Amount',
        cell: (info) =>
          `${currencyDetails?.symbol ?? '$'}${Number(info.getValue()).toFixed(2)}`,
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild disabled={!hasAdminAccess}>
              <Button variant='ghost' size='icon' className='size-8'>
                <MoreHorizontal className='size-4' />
                <span className='sr-only'>Actions</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              <EditUpcomingCharges
                invoice={row.original}
                studentId={studentId}
              />
              <CancelInvoice
                invoiceId={row.original.invoiceId}
                studentId={studentId}
              />
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      }),
    ],
    [currencyDetails?.symbol, hasAdminAccess, studentId],
  );

  const handleExportUpcomingCharges = () => {
    const headers = ['Date', 'Product', 'Type', 'Payment Frequency', 'Amount'];

    const formattedData = data?.map((invoice) => ({
      Date: invoice.upcomingChargeDate,
      Product: invoice.productName,
      Type: invoice.type,
      'Payment Frequency': invoice.paymentFrequency,
      Amount: invoice.amount,
    }));

    if (!formattedData?.length) return;

    exportToCSV(
      formattedData,
      headers,
      `upcoming-charges-${new Date().toISOString()}.csv`,
    );
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant='outline' className='w-full'>
          <Calendar />
          View Upcoming Charges
        </Button>
      </DialogTrigger>
      <DialogContent className='max-w-4xl'>
        <DialogHeader>
          <DialogTitle>Upcoming Charges</DialogTitle>
          <DialogDescription>
            View and manage upcoming charges for this student.
          </DialogDescription>
        </DialogHeader>
        <EnrollioTable
          totalLabel='Upcoming charges'
          filterHeader={(table) => (
            <EnrollioTableSearch
              table={table}
              placeholder='Search charges...'
            />
          )}
          data={data ?? []}
          columns={columns}
          getRowId={(row) => `${row.type}-${row.invoiceId}`}
          isUpdating={isLoading}
          emptyContent='No upcoming charges.'
          ariaLabel='Upcoming charges'
        />
        <DialogFooter>
          <Button
            variant='secondary'
            className='mr-auto'
            onClick={() => {
              handleExportUpcomingCharges();
            }}
          >
            <Download />
            Export Upcoming Charges
          </Button>
          <DialogClose asChild>
            <Button type='button' variant='outline'>
              Close
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
